import type { ApiErrorPayload } from '@mbs/shared';

/**
 * Every failure that reaches the UI is an `ApiErrorPayload`: a real reason, the
 * step, and the correlation id. Nothing in this file ever invents "please try
 * again in a moment".
 */
export class ApiClientError extends Error {
  readonly payload: ApiErrorPayload;
  /** True when the request never reached the Worker (offline, DNS, timeout). */
  readonly offline: boolean;

  constructor(payload: ApiErrorPayload, offline = false) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.payload = payload;
    this.offline = offline;
  }
}

export function newCorrelationId(): string {
  return crypto.randomUUID();
}

export interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  formData?: FormData;
  correlationId?: string;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const correlationId = options.correlationId ?? newCorrelationId();
  const headers: Record<string, string> = { 'X-Request-Id': correlationId };
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? (options.body !== undefined || options.formData ? 'POST' : 'GET'),
      headers,
      credentials: 'same-origin',
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiClientError(
      {
        error: true,
        code: navigator.onLine ? 'network' : 'offline',
        message: navigator.onLine
          ? `Could not reach the server (${cause instanceof Error ? cause.message : String(cause)}).`
          : 'You are offline. The entered data is kept and can be queued for later.',
        correlation_id: correlationId,
        timestamp: new Date().toISOString(),
      },
      true,
    );
  }

  if (!response.ok) throw await toError(response, correlationId);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function toError(response: Response, correlationId: string): Promise<ApiClientError> {
  const raw = await response.text().catch(() => '');
  try {
    const parsed = JSON.parse(raw) as ApiErrorPayload;
    if (parsed && typeof parsed.message === 'string') {
      return new ApiClientError({
        ...parsed,
        correlation_id: parsed.correlation_id || response.headers.get('X-Request-Id') || correlationId,
      });
    }
  } catch {
    /* fall through to the synthesised payload */
  }
  return new ApiClientError({
    error: true,
    code: 'http_error',
    message: `The server answered ${response.status} ${response.statusText}.`,
    correlation_id: response.headers.get('X-Request-Id') ?? correlationId,
    upstream_status: response.status,
    timestamp: new Date().toISOString(),
    raw: raw.slice(0, 1000) || undefined,
  });
}

export const api = {
  get: <T>(path: string, options: RequestOptions = {}) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'POST', body: body ?? {} }),
  upload: <T>(path: string, formData: FormData, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'POST', formData }),
  /** Opens a download in a new tab; the session cookie authorises it. */
  download: (path: string) => window.open(path, '_blank', 'noopener'),
};
